import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable, { applyPlugin } from "jspdf-autotable";
import { useCompany } from "../../context/CompanyContext";
import { showToast } from "../../utils/customToast";
import {
  FaSearch,
  FaFileExcel,
  FaFilePdf,
  FaPrint,
  FaClock,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import "./PurchaseRegister.css";
import { TableRowsSkeleton } from "../../Component/Skeleton";

try {
  applyPlugin(jsPDF);
} catch (e) {
  // autoTable plugin already applied or fallback
}

// Format numbers in standard Indian Rupee format
const formatINR = (val) => {
  if (val === null || val === undefined || isNaN(val)) return "";
  const num = Number(val);
  if (num === 0) return "";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function PurchaseRegister() {
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const BASE_API_URL =
    process.env.REACT_APP_BASE_API_URL ||
    process.env.REACT_APP_API_URL ||
    "http://localhost:5000/api";

  const companyId = useMemo(() => {
    return (
      selectedCompany?.CompID ||
      selectedCompany?.id ||
      sessionStorage.getItem("CompID") ||
      sessionStorage.getItem("companyId") ||
      "1147"
    );
  }, [selectedCompany]);

  const companyInfo = useMemo(() => {
    return {
      name:
        selectedCompany?.Name ||
        selectedCompany?.name ||
        selectedCompany?.CompanyName ||
        "DAZY TOURE TRAVEL'S",
      address:
        selectedCompany?.Address ||
        selectedCompany?.address ||
        "Opp. GPO, M.I. Road, Jaipur-302001",
      gstin: selectedCompany?.GSTNO || selectedCompany?.gstin || "",
      stateCode: selectedCompany?.StateCode || selectedCompany?.stateCode || "08",
    };
  }, [selectedCompany]);

  // Default dates: 1st of current month to today
  const defaultDates = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return {
      fromDate: `${y}-${m}-01`,
      toDate: `${y}-${m}-${d}`,
    };
  }, []);

  const [fromDate, setFromDate] = useState(defaultDates.fromDate);
  const [toDate, setToDate] = useState(defaultDates.toDate);
  const [activeFilters, setActiveFilters] = useState({
    fromDate: defaultDates.fromDate,
    toDate: defaultDates.toDate,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("detail"); // "short" or "detail"
  const [showNarration, setShowNarration] = useState(false);
  const [narrationFilter, setNarrationFilter] = useState("");
  const [liveData, setLiveData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Live timestamp for "As on" badge
  const asOnTime = useMemo(() => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const mNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const mon = mNames[now.getMonth()];
    const year = now.getFullYear();
    const hours = now.getHours();
    const mins = String(now.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const h12 = hours % 12 || 12;
    return `${day}-${mon}-${year} ${h12}:${mins} ${ampm}`;
  }, []);

  // Fetch purchase records if API exists
  const fetchPurchaseRecords = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE_API_URL}/reports/purchase-register/${companyId}?fromDate=${activeFilters.fromDate}&toDate=${activeFilters.toDate}`
      );
      if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
        setLiveData(res.data.data);
      } else {
        setLiveData([]);
      }
    } catch (err) {
      // Backend route not yet configured or empty: graceful fallback to initial records
      setLiveData([]);
    } finally {
      setLoading(false);
    }
  }, [BASE_API_URL, companyId, activeFilters]);

  useEffect(() => {
    fetchPurchaseRecords();
  }, [fetchPurchaseRecords]);

  const handleApplyFilter = () => {
    setActiveFilters({ fromDate, toDate });
    setCurrentPage(1);
  };

  const handleReset = () => {
    setFromDate(defaultDates.fromDate);
    setToDate(defaultDates.toDate);
    setActiveFilters({ fromDate: defaultDates.fromDate, toDate: defaultDates.toDate });
    setSearchTerm("");
    setNarrationFilter("");
    setShowNarration(false);
    setViewMode("detail");
    setCurrentPage(1);
  };

  // Filter records by date, search term, and narration (strictly liveData, 100% client-side without API calls)
  const filteredRecords = useMemo(() => {
    return liveData.filter((r) => {
      if (activeFilters.fromDate && r.rawDate && r.rawDate < activeFilters.fromDate) return false;
      if (activeFilters.toDate && r.rawDate && r.rawDate > activeFilters.toDate) return false;
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const matchInvoice = (r.invoiceNo || r.voucherNo) && (r.invoiceNo || r.voucherNo).toLowerCase().includes(term);
        const matchParty = (r.partyName || r.particulars) && (r.partyName || r.particulars).toLowerCase().includes(term);
        const matchDr = r.firstDebit && r.firstDebit.toLowerCase().includes(term);
        const matchCr = r.firstCredit && r.firstCredit.toLowerCase().includes(term);
        const matchNarr = r.narration && r.narration.toLowerCase().includes(term);
        if (!matchInvoice && !matchParty && !matchDr && !matchCr && !matchNarr) return false;
      }
      if (showNarration && narrationFilter.trim()) {
        const nTerm = narrationFilter.toLowerCase();
        if (!r.narration || !r.narration.toLowerCase().includes(nTerm)) return false;
      }
      return true;
    });
  }, [liveData, activeFilters, searchTerm, showNarration, narrationFilter]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, rowsPerPage, showNarration, narrationFilter]);

  // Totals for filtered records: Debit and Credit only
  const totals = useMemo(() => {
    let debit = 0;
    let credit = 0;

    filteredRecords.forEach((r) => {
      debit += Number(r.debit || 0);
      credit += Number(r.credit || 0);
    });

    return {
      debit: Math.round(debit * 100) / 100,
      credit: Math.round(credit * 100) / 100,
    };
  }, [filteredRecords]);

  // Paginated records
  const totalPages = Math.ceil(filteredRecords.length / rowsPerPage) || 1;
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredRecords.slice(start, start + rowsPerPage);
  }, [filteredRecords, currentPage, rowsPerPage]);

  // Export to Excel (.xlsx) matching Journal Register
  const handleExportExcel = () => {
    try {
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compDetails = `Address: ${companyInfo.address || ""}  |  GSTIN: ${companyInfo.gstin || ""}  |  State: ${companyInfo.stateCode || "08"}`;
      const reportTitle = "PURCHASE REGISTER";
      const periodSubtitle = `Period: ${activeFilters.fromDate} to ${activeFilters.toDate}  |  Generated: ${asOnTime}`;

      const headers = [
        "Date",
        "Invoice No.",
        "Particulars / Narration",
        "Debit (INR)",
        "Credit (INR)",
      ];

      const isShort = viewMode === "short";
      const dataRows = [];
      filteredRecords.forEach((r) => {
        const debitLines = (r.lines || []).filter((l) => l.type === "Dr");
        const creditLines = (r.lines || []).filter((l) => l.type === "Cr");

        if (debitLines.length === 0 && creditLines.length === 0) {
          const firstDebit = r.firstDebit || r.particulars || "Purchase Account";
          const firstCredit = r.firstCredit ? `  ${r.firstCredit}` : `  ${r.partyName || r.particulars || "Party"}`;
          dataRows.push([
            r.date,
            r.invoiceNo || r.voucherNo || "",
            firstDebit,
            Number(r.debit) > 0 ? Number(r.debit) : "",
            "",
          ]);
          if (!isShort && Number(r.credit) > 0) {
            dataRows.push([
              "",
              "",
              firstCredit,
              "",
              Number(r.credit),
            ]);
          }
        } else {
          if (debitLines.length === 0) {
            dataRows.push([
              r.date,
              r.invoiceNo || r.voucherNo || "",
              r.firstDebit || r.particulars || "Purchase Account",
              Number(r.debit) > 0 ? Number(r.debit) : "",
              "",
            ]);
          } else {
            debitLines.forEach((dr, idx) => {
              const drAmt = Number(dr.debit) || Number(r.debit) || 0;
              dataRows.push([
                idx === 0 ? r.date : "",
                idx === 0 ? (r.invoiceNo || r.voucherNo || "") : "",
                dr.ledgerName,
                drAmt > 0 ? drAmt : "",
                "",
              ]);
            });
          }

          if (!isShort) {
            creditLines.forEach((cr, idx) => {
              const crAmt = Number(cr.credit) || Number(r.credit) || 0;
              dataRows.push([
                debitLines.length === 0 && idx === 0 ? r.date : "",
                debitLines.length === 0 && idx === 0 ? (r.invoiceNo || r.voucherNo || "") : "",
                `  ${cr.ledgerName}`,
                "",
                crAmt > 0 ? crAmt : "",
              ]);
            });
          }
        }

        if (showNarration && r.narration) {
          dataRows.push([
            "",
            "",
            `  (Narration: ${r.narration})`,
            "",
            "",
          ]);
        }
      });

      const totalRow = [
        "Total",
        "",
        "",
        Number(totals.debit) || 0,
        Number(totals.credit) || 0,
      ];

      const wsData = [
        [activeCompName],
        [compDetails],
        [reportTitle],
        [periodSubtitle],
        [],
        headers,
        ...dataRows,
        totalRow,
        [],
        ["etour - Design & Develop by Response Infoway"],
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(wsData);
      worksheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: 4 } },
        { s: { r: 3, c: 0 }, e: { r: 3, c: 4 } },
      ];

      worksheet["!cols"] = [
        { wch: 15 }, // Date
        { wch: 18 }, // Invoice No
        { wch: 55 }, // Particulars / Narration
        { wch: 20 }, // Debit
        { wch: 20 }, // Credit
      ];

      // Format number cells in worksheet with standard Indian 2-decimal format
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let R = 5; R <= range.e.r; ++R) {
        const cellDr = worksheet[XLSX.utils.encode_cell({ r: R, c: 3 })];
        if (cellDr && typeof cellDr.v === "number") {
          cellDr.z = "#,##0.00";
        }
        const cellCr = worksheet[XLSX.utils.encode_cell({ r: R, c: 4 })];
        if (cellCr && typeof cellCr.v === "number") {
          cellCr.z = "#,##0.00";
        }
      }


      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Purchase_Register");
      const fileName = `Purchase_Register_${activeFilters.fromDate}_to_${activeFilters.toDate}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      showToast.success("Excel exported successfully");
    } catch (err) {
      console.error(err);
      showToast.error("Failed to export Excel");
    }
  };

  // Export to PDF matching Journal Register
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF("l", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compLine2 = [
        companyInfo.address,
        companyInfo.gstin ? `GSTIN: ${companyInfo.gstin}` : "",
        companyInfo.stateCode ? `State Code: ${companyInfo.stateCode}` : "",
      ]
        .filter(Boolean)
        .join("   |   ");
      const reportTitle = "PURCHASE REGISTER";

      // 1. Company Name Header
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.setTextColor(0, 0, 0);
      doc.text(activeCompName, pageWidth / 2, 14, { align: "center" });

      // 2. Company Details
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(40, 40, 40);
      doc.text(compLine2, pageWidth / 2, 19, { align: "center" });

      // 3. Report Title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10.5);
      doc.setTextColor(0, 0, 0);
      doc.text(reportTitle, pageWidth / 2, 26, { align: "center" });
      const titleWidth = doc.getTextWidth(reportTitle);
      doc.setLineWidth(0.75);
      doc.setDrawColor(0, 0, 0);
      doc.line(pageWidth / 2 - titleWidth / 2, 27.5, pageWidth / 2 + titleWidth / 2, 27.5);

      // 4. Divider Line
      doc.setLineWidth(1);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 31, pageWidth - 14, 31);

      // 5. Meta Information Bar
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(0, 0, 0);
      doc.text(`Period: ${activeFilters.fromDate} to ${activeFilters.toDate}`, 14, 36);
      doc.text(`Generated: ${asOnTime}`, pageWidth - 14, 36, { align: "right" });

      // 6. Divider Line below Meta bar
      doc.setLineWidth(0.5);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 39, pageWidth - 14, 39);

      const isShort = viewMode === "short";
      const tableRows = filteredRecords.map((r) => {
        const firstDebit = r.firstDebit || (r.lines ? r.lines.find((l) => l.type === "Dr")?.ledgerName : null) || r.particulars || "Purchase Account";
        const firstCredit = r.firstCredit || (r.lines ? r.lines.find((l) => l.type === "Cr")?.ledgerName : null) || r.partyName;
        const parts = [];
        if (firstDebit) parts.push(firstDebit);
        if (!isShort && firstCredit) parts.push(`  ${firstCredit}`);
        if (!firstDebit && !firstCredit) parts.push(r.partyName || r.particulars);
        if (showNarration && r.narration) {
          parts.push(`  (Narration: ${r.narration})`);
        }

        const debitStr = firstDebit && Number(r.debit) > 0 ? formatINR(r.debit) : "";
        const creditStr = !isShort && firstCredit && Number(r.credit) > 0 ? formatINR(r.credit) : "";

        const drCol = (!isShort && firstCredit) ? (debitStr ? `${debitStr}\n` : "") : debitStr;
        const crCol = firstDebit ? (creditStr ? `\n${creditStr}` : "") : creditStr;

        return [
          r.date,
          r.invoiceNo || r.voucherNo,
          parts.join("\n"),
          drCol,
          crCol,
        ];
      });

      tableRows.push([
        "",
        "",
        "Total",
        formatINR(totals.debit),
        formatINR(totals.credit),
      ]);

      autoTable(doc, {
        head: [
          [
            "Date",
            "Invoice No.",
            "Party Name",
            "Debit (INR)",
            "Credit (INR)",
          ],
        ],
        body: tableRows,
        startY: 42,
        theme: "striped",
        headStyles: {
          fillColor: [6, 78, 72], // --primary-teal
          textColor: 255,
          fontStyle: "bold",
          fontSize: 8.5,
          halign: "center",
        },
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          textColor: 30,
        },
        columnStyles: {
          0: { halign: "center", cellWidth: 30 },
          1: { halign: "center", cellWidth: 35 },
          2: { halign: "left" },
          3: { halign: "right", cellWidth: 40 },
          4: { halign: "right", cellWidth: 40 },
        },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.setTextColor(120, 120, 120);
          doc.text(
            "etour - Design & Develop by Response Infoway",
            14,
            doc.internal.pageSize.getHeight() - 8
          );
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            pageWidth - 14,
            doc.internal.pageSize.getHeight() - 8,
            { align: "right" }
          );
        },
      });

      const fileName = `Purchase_Register_${activeFilters.fromDate}_to_${activeFilters.toDate}.pdf`;
      doc.save(fileName);
      showToast.success("PDF exported successfully");
    } catch (err) {
      console.error(err);
      showToast.error("Failed to export PDF");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="pr_page_wrapper">
      {/* Formal Printable Header (Visible during Print only) */}
      <div className="pr_print_header d-none d-print-block text-center mb-3">
        <h2
          className="mb-0 fw-bold text-uppercase"
          style={{ fontSize: "18px", color: "#000000", letterSpacing: "0.5px" }}
        >
          {companyInfo.name}
        </h2>
        <div style={{ fontSize: "11px", color: "#333333", marginTop: "2px" }}>
          {[
            companyInfo.address,
            companyInfo.gstin ? `GSTIN: ${companyInfo.gstin}` : "",
            companyInfo.stateCode ? `State Code: ${companyInfo.stateCode}` : "",
          ]
            .filter(Boolean)
            .join("   |   ")}
        </div>
        <div
          className="fw-bold mt-2"
          style={{ fontSize: "13px", color: "#000000" }}
        >
          PURCHASE REGISTER
        </div>
        <div style={{ fontSize: "10.5px", color: "#555555" }}>
          Period: {activeFilters.fromDate} to {activeFilters.toDate} | As on: {asOnTime}
        </div>
      </div>

      {/* Top Header & Breadcrumb */}
      <div className="pr_header_section">
        <div className="pr_breadcrumb_bar">
          Reports <span>&gt;</span> <strong>Purchase Register</strong>
        </div>
        <div className="pr_as_on_badge">
          <FaClock />
          As on: {asOnTime}
        </div>
      </div>

      {/* Filter Card */}
      <div className="pr_filter_card">
        <div className="pr_filter_bar">
          {/* From Date */}
          <div className="pr_filter_group">
            <label className="pr_filter_label">
              From Date<span className="required_star">*</span>:
            </label>
            <input
              type="date"
              className="filter-input"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={{ width: 130 }}
            />
          </div>

          {/* To Date */}
          <div className="pr_filter_group">
            <label className="pr_filter_label">
              To Date<span className="required_star">*</span>:
            </label>
            <input
              type="date"
              className="filter-input"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={{ width: 130 }}
            />
          </div>

          {/* Format Toggle (Short / Detail) */}
          <div className="pr_filter_group">
            <span className="pr_filter_label">Format:</span>
            <div className="pr_format_toggle">
              <button
                type="button"
                className={`pr_format_btn ${viewMode === "short" ? "active" : ""}`}
                onClick={() => setViewMode("short")}
              >
                Short
              </button>
              <button
                type="button"
                className={`pr_format_btn ${viewMode === "detail" ? "active" : ""}`}
                onClick={() => setViewMode("detail")}
              >
                Detail
              </button>
            </div>
          </div>

          {/* Narration Filter Checkbox + Search */}
          <div className="pr_filter_group pr_narration_box">
            <label className="d-flex align-items-center gap-1 mb-0" style={{ cursor: "pointer", fontSize: "11px", fontWeight: 600, color: "#4a5568" }}>
              <input
                type="checkbox"
                className="pr_checkbox"
                checked={showNarration}
                onChange={(e) => setShowNarration(e.target.checked)}
              />
              Narration
            </label>
            {showNarration && (
              <input
                type="text"
                className="filter-input"
                style={{ width: "120px", height: "26px", fontSize: "11px" }}
                placeholder="Search narration..."
                value={narrationFilter}
                onChange={(e) => setNarrationFilter(e.target.value)}
              />
            )}
          </div>

          {/* Filter Buttons */}
          <div className="pr_filter_buttons">
            <button
              type="button"
              className="pr_btn_apply"
              onClick={handleApplyFilter}
              disabled={loading}
            >
              View
            </button>
            <button type="button" className="pr_btn_reset" onClick={handleReset}>
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="pr_table_card">
        {/* Table Toolbar */}
        <div className="pr_table_toolbar">
          <h3 className="pr_table_title">
            Purchase Register ({activeFilters.fromDate} to {activeFilters.toDate})
          </h3>

          <div className="pr_toolbar_right">
            {/* Search Box */}
            <div className="pr_search_box">
              <FaSearch className="pr_search_icon" />
              <input
                type="text"
                className="pr_search_input"
                placeholder="Search invoice no. or party..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Action Buttons */}
            <button
              type="button"
              className="pr_export_btn btn_excel"
              onClick={handleExportExcel}
              title="Export to Microsoft Excel"
            >
              <FaFileExcel />
              Export Excel
            </button>
            <button
              type="button"
              className="pr_export_btn btn_pdf"
              onClick={handleExportPDF}
              title="Export to PDF"
            >
              <FaFilePdf />
              Export PDF
            </button>
            <button
              type="button"
              className="pr_export_btn btn_print"
              onClick={handlePrint}
              title="Print Summary"
            >
              <FaPrint />
              Print
            </button>
          </div>
        </div>

        {/* Table Wrapper */}
        <div className="pr_table_wrapper">
          <table className="pr_custom_table">
            <thead>
              <tr>
                <th style={{ width: "110px", textAlign: "center" }}>Date</th>
                <th style={{ width: "130px", textAlign: "center" }}>Invoice No.</th>
                <th>Party Name</th>
                <th style={{ width: "150px", textAlign: "right" }}>Debit (₹)</th>
                <th style={{ width: "150px", textAlign: "right" }}>Credit (₹)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableRowsSkeleton columns={5} rows={5} />
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", padding: "20px", color: "#a0aec0" }}>
                    No purchase records found for the selected period.
                  </td>
                </tr>
              ) : (
                paginatedData.map((row) => {
                  const isShort = viewMode === "short";
                  const firstDebit = row.firstDebit || (row.lines ? row.lines.find((l) => l.type === "Dr")?.ledgerName : null) || row.particulars || "Purchase Account";
                  const firstCredit = row.firstCredit || (row.lines ? row.lines.find((l) => l.type === "Cr")?.ledgerName : null) || row.partyName;
                  const hasDebit = !!firstDebit;
                  const hasCredit = !isShort && !!firstCredit;

                  return (
                    <tr
                      key={row.id || row.voucherId}
                      onClick={() => {
                        if (row.voucherId) {
                          navigate(
                            `/transactions/modify-voucher?voucherId=${row.voucherId}`
                          );
                        } else {
                          navigate(
                            `/transactions/modify-voucher?voucherNo=${encodeURIComponent(
                              row.invoiceNo || row.voucherNo
                            )}`
                          );
                        }
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <td style={{ textAlign: "center", color: "#4a5568", whiteSpace: "nowrap" }}>
                        {row.date}
                      </td>
                      <td style={{ textAlign: "center", fontWeight: "600", whiteSpace: "nowrap" }}>
                        <span
                          className="pr_voucher_click_link"
                          title="Click to view & modify this voucher in Transactions"
                          style={{
                            cursor: "pointer",
                            color: "var(--primary-teal)",
                            textDecoration: "none",
                            fontWeight: 700,
                          }}
                        >
                          {row.invoiceNo || row.voucherNo}
                        </span>
                      </td>
                      <td style={{ color: "#2d3748", fontWeight: "500" }}>
                        <div className="pr_particulars_cell">
                          {hasDebit && (
                            <div className="pr_debit_ledger">{firstDebit}</div>
                          )}
                          {hasCredit && (
                            <div className="pr_credit_ledger">{firstCredit}</div>
                          )}
                          {!hasDebit && !hasCredit && (
                            <div className="pr_debit_ledger">{row.partyName || row.particulars}</div>
                          )}
                          {showNarration && row.narration && (
                            <div
                              className="pr_narration_text"
                              style={{
                                fontSize: "10.5px",
                                color: "#64748b",
                                fontStyle: "italic",
                                marginTop: "2px",
                              }}
                            >
                              Narration: {row.narration}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="pr_table_num">
                        <div className="pr_amount_cell">
                          {hasDebit && (
                            <div className="pr_amt_val">
                              {Number(row.debit) > 0 ? formatINR(row.debit) : ""}
                            </div>
                          )}
                          {hasCredit && <div className="pr_dash_amt"></div>}
                          {!hasDebit && !hasCredit && (
                            <div className="pr_amt_val">
                              {Number(row.debit) > 0 ? formatINR(row.debit) : ""}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="pr_table_num">
                        <div className="pr_amount_cell">
                          {hasDebit && <div className="pr_dash_amt"></div>}
                          {hasCredit && (
                            <div className="pr_amt_val">
                              {Number(row.credit) > 0 ? formatINR(row.credit) : ""}
                            </div>
                          )}
                          {!hasDebit && !hasCredit && (
                            <div className="pr_amt_val">
                              {Number(row.credit) > 0 ? formatINR(row.credit) : ""}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {filteredRecords.length > 0 && (
              <tfoot>
                <tr className="pr_table_total_row">
                  <td style={{ textAlign: "center" }}></td>
                  <td style={{ textAlign: "center" }}></td>
                  <td style={{ fontWeight: "700" }}>Total</td>
                  <td className="pr_table_num" style={{ fontWeight: "700", color: "#166534" }}>
                    {formatINR(totals.debit)}
                  </td>
                  <td className="pr_table_num" style={{ fontWeight: "700", color: "#166534" }}>
                    {formatINR(totals.credit)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination Row */}
        {filteredRecords.length > 0 && (
          <div className="pr_pagination_row">
            <div>
              Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
              {Math.min(currentPage * rowsPerPage, filteredRecords.length)} of{" "}
              {filteredRecords.length} records
            </div>

            <div className="pr_page_controls">
              <button
                type="button"
                className="pr_page_btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                <FaChevronLeft />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((p, idx, arr) => {
                  const showEllipsis = idx > 0 && p - arr[idx - 1] > 1;
                  return (
                    <React.Fragment key={p}>
                      {showEllipsis && <span style={{ padding: "0 3px" }}>...</span>}
                      <button
                        type="button"
                        className={`pr_page_btn ${currentPage === p ? "active" : ""}`}
                        onClick={() => setCurrentPage(p)}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button
                type="button"
                className="pr_page_btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                <FaChevronRight />
              </button>

              <span style={{ marginLeft: 8, marginRight: 4 }}>Rows per page:</span>
              <select
                className="pr_rows_select"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* DEDICATED PRINT CONTAINER (Hidden on screen, Active during @media print) */}
      <div className="pr_print_area d-none">
        {/* Company Header */}
        <div
          style={{
            textAlign: "center",
            borderBottom: "2px solid #000000",
            paddingBottom: "8px",
            marginBottom: "10px",
          }}
        >
          <h2
            style={{
              margin: "0 0 3px 0",
              fontSize: "19px",
              fontWeight: "bold",
              textTransform: "uppercase",
              color: "#000000",
            }}
          >
            {companyInfo.name}
          </h2>
          <div style={{ fontSize: "11px", color: "#222222", lineHeight: 1.4 }}>
            {companyInfo.address && `${companyInfo.address}  |  `}
            <strong>GSTIN:</strong> {companyInfo.gstin}
            {companyInfo.stateCode && `  |  State Code: ${companyInfo.stateCode}`}
          </div>
          <div
            style={{
              marginTop: "6px",
              fontSize: "13px",
              fontWeight: "bold",
              color: "#000000",
            }}
          >
            PURCHASE REGISTER
          </div>
        </div>

        {/* Print Filter Meta */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "11px",
            marginBottom: "8px",
            borderBottom: "1px solid #000000",
            paddingBottom: "4px",
          }}
        >
          <div>
            <strong>Period:</strong> {activeFilters.fromDate} to {activeFilters.toDate}
          </div>
          <div style={{ textAlign: "right" }}>
            <strong>Date:</strong> {asOnTime}  |  <strong>Records:</strong> {filteredRecords.length}
          </div>
        </div>

        {/* Table */}
        <table className="pr_print_table">
          <thead>
            <tr>
              <th style={{ width: "15%", textAlign: "center" }}>Date</th>
              <th style={{ width: "15%", textAlign: "center" }}>Invoice No.</th>
              <th style={{ width: "40%" }}>Party Name</th>
              <th style={{ width: "15%", textAlign: "right" }}>Debit (₹)</th>
              <th style={{ width: "15%", textAlign: "right" }}>Credit (₹)</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.map((r, idx) => {
              const isShort = viewMode === "short";
              const firstDebit = r.firstDebit || (r.lines ? r.lines.find((l) => l.type === "Dr")?.ledgerName : null) || r.particulars || "Purchase Account";
              const firstCredit = r.firstCredit || (r.lines ? r.lines.find((l) => l.type === "Cr")?.ledgerName : null) || r.partyName;
              const hasDebit = !!firstDebit;
              const hasCredit = !isShort && !!firstCredit;

              return (
                <tr key={r.id || idx} className={idx % 2 === 1 ? "print_row_alt" : ""}>
                  <td style={{ textAlign: "center", verticalAlign: "top" }}>{r.date}</td>
                  <td style={{ textAlign: "center", fontWeight: 600, verticalAlign: "top" }}>
                    {r.invoiceNo || r.voucherNo}
                  </td>
                  <td style={{ verticalAlign: "top" }}>
                    <div className="pr_particulars_cell">
                      {hasDebit && <div className="pr_debit_ledger">{firstDebit}</div>}
                      {hasCredit && <div className="pr_credit_ledger" style={{ paddingLeft: "12px", color: "#1a202c", fontWeight: 600 }}>{firstCredit}</div>}
                      {!hasDebit && !hasCredit && <div className="pr_debit_ledger">{r.partyName || r.particulars}</div>}
                      {showNarration && r.narration && (
                        <div style={{ fontSize: "10px", color: "#64748b", fontStyle: "italic", marginTop: "2px" }}>
                          Narration: {r.narration}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ textAlign: "right", verticalAlign: "top" }}>
                    <div className="pr_amount_cell">
                      {hasDebit && <div className="pr_amt_val">{Number(r.debit) > 0 ? formatINR(r.debit) : ""}</div>}
                      {hasCredit && <div className="pr_dash_amt"></div>}
                      {!hasDebit && !hasCredit && <div className="pr_amt_val">{Number(r.debit) > 0 ? formatINR(r.debit) : ""}</div>}
                    </div>
                  </td>
                  <td style={{ textAlign: "right", verticalAlign: "top" }}>
                    <div className="pr_amount_cell">
                      {hasDebit && <div className="pr_dash_amt"></div>}
                      {hasCredit && <div className="pr_amt_val">{Number(r.credit) > 0 ? formatINR(r.credit) : ""}</div>}
                      {!hasDebit && !hasCredit && <div className="pr_amt_val">{Number(r.credit) > 0 ? formatINR(r.credit) : ""}</div>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="print_total_row">
              <td style={{ textAlign: "center" }}></td>
              <td style={{ textAlign: "center" }}></td>
              <td style={{ fontWeight: "bold" }}>Total</td>
              <td style={{ textAlign: "right", fontWeight: "bold" }}>
                {formatINR(totals.debit)}
              </td>
              <td style={{ textAlign: "right", fontWeight: "bold" }}>
                {formatINR(totals.credit)}
              </td>
            </tr>
          </tfoot>
        </table>

        {/* Footer */}
        <div className="pr_print_footer">
          <span>etour - Design & Develop by Response Infoway</span>
          <span>Printed on: {asOnTime}</span>
        </div>
      </div>
    </div>
  );
}
